// Iter 83 / Phase E4 Sprint 1 — Loans Console.
// Operator surface for issuing, approving, disbursing, and foreclosing
// employee loans. Implements D14 (4-eye), D15 (foreclose modes),
// D16 (outstanding visible per loan).
import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Plus, CheckCircle, XCircle, ArrowsClockwise, Bank, MagnifyingGlass,
    Money, ListBullets, FileText, Pause, Play,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";

const STATUS_TONE = {
    Applied: "bg-amber-100 text-amber-800",
    Approved: "bg-sky-100 text-sky-800",
    Disbursed: "bg-violet-100 text-violet-800",
    Active: "bg-emerald-100 text-emerald-800",
    Paused: "bg-amber-100 text-amber-900",
    Closed: "bg-slate-200 text-slate-700",
    Foreclosed: "bg-emerald-100 text-emerald-700",
    Defaulted: "bg-rose-100 text-rose-800",
    Rejected: "bg-zinc-100 text-zinc-600",
};

const fmtMoney = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function LoansConsole() {
    const { can, user } = useAuth();
    const canIssue = can("loans:admin");
    const canApprove = can("loans:admin") || can("finance:approve");
    const [items, setItems] = useState([]);
    const [policies, setPolicies] = useState([]);
    const [meta, setMeta] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    // Issuance dialog
    const [issueOpen, setIssueOpen] = useState(false);
    const [issueForm, setIssueForm] = useState({
        employee_id: "", policy_id: "", principal: 100000, tenure_months: 12,
        annual_rate_pct: "", purpose: "",
    });
    // Detail dialog
    const [detail, setDetail] = useState(null);
    const [schedule, setSchedule] = useState([]);
    const [audit, setAudit] = useState([]);
    // Foreclose dialog
    const [forecloseOpen, setForecloseOpen] = useState(false);
    const [forecloseLoan, setForecloseLoan] = useState(null);
    const [forecloseQuote, setForecloseQuote] = useState(null);
    const [forecloseInstalment, setForecloseInstalment] = useState(1);
    const [forecloseMode, setForecloseMode] = useState("");

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            const [list, pol, m, emp] = await Promise.all([
                api.get("/loans/list", { params }),
                api.get("/loans/policy/list", { params: { is_active: true } }),
                api.get("/loans/meta"),
                api.get("/hr/employee/list", { params: { limit: 500 } }).catch(() => ({ data: { items: [] } })),
            ]);
            setItems(list.data.items);
            setPolicies(pol.data.items);
            setMeta(m.data);
            setEmployees(emp.data.items || []);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load loans"));
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);
    useEffect(() => { reload(); }, [reload]);

    const filtered = useMemo(() => {
        if (!search) return items;
        const q = search.toLowerCase();
        return items.filter((it) =>
            (it.id || "").toLowerCase().includes(q)
            || (it.employee_name_snapshot || "").toLowerCase().includes(q)
            || (it.policy_code_snapshot || "").toLowerCase().includes(q)
        );
    }, [items, search]);

    const issueLoan = async () => {
        if (!issueForm.employee_id || !issueForm.policy_id) {
            toast.error("Employee and Policy are required");
            return;
        }
        try {
            const payload = {
                ...issueForm,
                principal: Number(issueForm.principal),
                tenure_months: Number(issueForm.tenure_months),
            };
            if (issueForm.annual_rate_pct === "") delete payload.annual_rate_pct;
            else payload.annual_rate_pct = Number(issueForm.annual_rate_pct);
            const { data } = await api.post("/loans/apply", payload);
            toast.success(`Loan ${data.id} applied. EMI: ${fmtMoney(data.emi)}`);
            setIssueOpen(false);
            setIssueForm({ employee_id: "", policy_id: "", principal: 100000, tenure_months: 12, annual_rate_pct: "", purpose: "" });
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Issue failed"));
        }
    };

    const openDetail = async (loan) => {
        setDetail(loan);
        setSchedule([]);
        setAudit([]);
        try {
            const [s, a] = await Promise.all([
                api.get(`/loans/${loan.id}/schedule`),
                api.get(`/loans/${loan.id}/audit`),
            ]);
            setSchedule(s.data.items);
            setAudit(a.data.items);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load detail"));
        }
    };

    const approveLoan = async (loan) => {
        if (!window.confirm(`Approve loan ${loan.id} for ${loan.employee_name_snapshot}?`)) return;
        try {
            await api.post(`/loans/${loan.id}/approve`, { approver_note: "Approved via console" });
            toast.success("Approved");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Approve failed"));
        }
    };

    const rejectLoan = async (loan) => {
        const reason = window.prompt("Reject reason:");
        if (!reason) return;
        try {
            await api.post(`/loans/${loan.id}/reject`, { reason });
            toast.success("Rejected");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Reject failed"));
        }
    };

    const disburseLoan = async (loan) => {
        const ref = window.prompt("Bank reference number (NEFT/IMPS):", "");
        if (ref === null) return;
        try {
            await api.post(`/loans/${loan.id}/disburse`, { reference_number: ref });
            toast.success("Disbursed");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Disburse failed"));
        }
    };

    // Iter 111 · P2.B — Pause / Resume EMI recovery
    const pauseLoan = async (loan) => {
        const reason = window.prompt("Pause reason (min 1 char):", "");
        if (!reason || !reason.trim()) return;
        try {
            await api.post(`/loans/${loan.id}/pause`, { reason: reason.trim() });
            toast.success("Loan paused — no EMI will be deducted until resumed");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Pause failed"));
        }
    };

    const resumeLoan = async (loan) => {
        const reason = window.prompt("Resume note (optional):", "");
        if (reason === null) return;
        const extend = window.confirm(
            "Extend tenure by paused cycles? OK = Yes (recommended), Cancel = No",
        );
        try {
            await api.post(`/loans/${loan.id}/resume`, {
                reason: (reason || "").trim(),
                extend_tenure: extend,
            });
            toast.success("Loan resumed");
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Resume failed"));
        }
    };

    const openForeclose = async (loan) => {
        setForecloseLoan(loan);
        setForecloseQuote(null);
        setForecloseInstalment(loan.instalments_paid || 0);
        setForecloseMode(loan.policy_foreclose_mode_snapshot || "forgive_unaccrued");
        setForecloseOpen(true);
    };

    const fetchForecloseQuote = useCallback(async () => {
        if (!forecloseLoan) return;
        try {
            const params = { as_of_instalment: forecloseInstalment };
            if (forecloseMode) params.mode = forecloseMode;
            const { data } = await api.get(`/loans/${forecloseLoan.id}/foreclose-quote`, { params });
            setForecloseQuote(data);
        } catch (err) {
            setForecloseQuote(null);
            toast.error(formatApiError(err, "Quote failed"));
        }
    }, [forecloseLoan, forecloseInstalment, forecloseMode]);

    useEffect(() => {
        if (forecloseOpen) fetchForecloseQuote();
    }, [forecloseOpen, fetchForecloseQuote]);

    const submitForeclose = async () => {
        if (!forecloseLoan || !forecloseQuote) return;
        if (!window.confirm(`Confirm foreclose of ${forecloseLoan.id} for ${fmtMoney(forecloseQuote.foreclose_amount)}?`)) return;
        try {
            await api.post(`/loans/${forecloseLoan.id}/foreclose`, {
                as_of_instalment: forecloseInstalment,
                mode: forecloseMode,
                note: "Foreclosed via console",
            });
            toast.success("Foreclosed");
            setForecloseOpen(false);
            setDetail(null);
            reload();
        } catch (err) {
            toast.error(formatApiError(err, "Foreclose failed"));
        }
    };

    return (
        <div className="px-6 sm:px-8 py-6 max-w-[1600px]" data-testid="loans-console-page">
            <PageHeader
                eyebrow="Loans & Advances"
                title="Loans Console"
                description={`${items.length} loan${items.length === 1 ? "" : "s"} on file · D14 4-eye approval enforced`}
                actions={canIssue ? (
                    <Button onClick={() => setIssueOpen(true)} size="sm" data-testid="loan-new-btn">
                        <Plus size={14} className="mr-1" /> New Loan
                    </Button>
                ) : null}
            />
            <section className="mt-5 bg-white rounded-xl border border-slate-200 p-4">
                <div className="flex gap-3 flex-wrap items-end">
                    <div className="flex-1 min-w-[240px]">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Search</Label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input className="pl-9" placeholder="Loan ID, employee, or policy code…"
                                value={search} onChange={(e) => setSearch(e.target.value)}
                                data-testid="loans-search" />
                        </div>
                    </div>
                    <div className="w-48">
                        <Label className="text-[10px] uppercase text-slate-500 font-semibold">Status</Label>
                        <select className="w-full border rounded-md text-sm h-9 px-2"
                            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                            data-testid="loans-status-filter">
                            <option value="">All</option>
                            {(meta?.loan_states || []).map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </div>
                </div>
            </section>
            <section className="mt-4 bg-white rounded-xl border border-slate-200 overflow-x-auto" data-testid="loans-table">
                {loading ? <div className="text-center py-12 text-sm text-slate-400">Loading…</div> : (
                    <table className="w-full text-sm min-w-[1200px]">
                        <thead className="bg-slate-50 text-slate-600 text-xs">
                            <tr>
                                <th className="text-left px-3 py-2 font-medium">Loan ID</th>
                                <th className="text-left px-3 py-2 font-medium">Employee</th>
                                <th className="text-left px-3 py-2 font-medium">Policy</th>
                                <th className="text-right px-3 py-2 font-medium">Principal</th>
                                <th className="text-right px-3 py-2 font-medium">EMI</th>
                                <th className="text-right px-3 py-2 font-medium">Tenure</th>
                                <th className="text-right px-3 py-2 font-medium">Rate</th>
                                <th className="text-right px-3 py-2 font-medium">Outstanding</th>
                                <th className="text-left px-3 py-2 font-medium">Status</th>
                                <th className="text-left px-3 py-2 font-medium">Applied</th>
                                <th className="text-left px-3 py-2 font-medium"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((it) => (
                                <tr key={it.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`loan-row-${it.id}`}>
                                    <td className="px-3 py-2 font-mono text-xs">{it.id}</td>
                                    <td className="px-3 py-2">{it.employee_name_snapshot}</td>
                                    <td className="px-3 py-2 text-xs font-mono">{it.policy_code_snapshot}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.principal)}</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.emi)}</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{it.tenure_months}m</td>
                                    <td className="px-3 py-2 text-right font-mono text-xs">{Number(it.annual_rate_pct).toFixed(2)}%</td>
                                    <td className="px-3 py-2 text-right font-mono">{fmtMoney(it.outstanding_principal)}</td>
                                    <td className="px-3 py-2">
                                        <Badge className={`text-[10px] ${STATUS_TONE[it.status] || ""}`}>{it.status}</Badge>
                                    </td>
                                    <td className="px-3 py-2 text-xs text-slate-500">{(it.applied_at || "").slice(0, 10)}</td>
                                    <td className="px-3 py-2">
                                        <Button variant="ghost" size="sm" onClick={() => openDetail(it)} data-testid={`loan-view-${it.id}`}>
                                            <ListBullets size={14} />
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr><td colSpan={11} className="text-center py-10 text-sm text-slate-400 italic">No loans match the current filter.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </section>

            {/* Issue dialog */}
            <Dialog open={issueOpen} onOpenChange={setIssueOpen}>
                <DialogContent className="max-w-xl" data-testid="loan-issue-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2"><Money size={18} /> Issue New Loan</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="col-span-2">
                            <Label>Employee *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={issueForm.employee_id}
                                onChange={(e) => setIssueForm({ ...issueForm, employee_id: e.target.value })}
                                data-testid="loan-issue-emp">
                                <option value="">— Select —</option>
                                {employees.map((e) => (
                                    <option key={e.id} value={e.id}>{e.full_name} ({e.id})</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-span-2">
                            <Label>Policy *</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={issueForm.policy_id}
                                onChange={(e) => {
                                    const pol = policies.find((p) => p.id === e.target.value);
                                    setIssueForm({
                                        ...issueForm,
                                        policy_id: e.target.value,
                                        annual_rate_pct: pol ? "" : issueForm.annual_rate_pct,
                                    });
                                }}
                                data-testid="loan-issue-policy">
                                <option value="">— Select —</option>
                                {policies.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.code} · {fmtMoney(p.max_principal)} / {p.max_tenure_months}m / {p.default_annual_rate_pct}%
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <Label>Principal (₹) *</Label>
                            <Input type="number" min="1" value={issueForm.principal}
                                onChange={(e) => setIssueForm({ ...issueForm, principal: e.target.value })}
                                data-testid="loan-issue-principal" />
                        </div>
                        <div>
                            <Label>Tenure (months) *</Label>
                            <Input type="number" min="1" max="120" value={issueForm.tenure_months}
                                onChange={(e) => setIssueForm({ ...issueForm, tenure_months: e.target.value })}
                                data-testid="loan-issue-tenure" />
                        </div>
                        <div>
                            <Label>Rate (% p.a.) — leave blank for policy default</Label>
                            <Input type="number" step="0.01" min="0" max="50" value={issueForm.annual_rate_pct}
                                onChange={(e) => setIssueForm({ ...issueForm, annual_rate_pct: e.target.value })}
                                placeholder="Defaults to policy" />
                        </div>
                        <div>
                            <Label>Purpose</Label>
                            <Input value={issueForm.purpose}
                                onChange={(e) => setIssueForm({ ...issueForm, purpose: e.target.value })}
                                placeholder="optional" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setIssueOpen(false)}>Cancel</Button>
                        <Button onClick={issueLoan} data-testid="loan-issue-submit">Apply Loan</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Detail dialog */}
            <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
                <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="loan-detail-dialog">
                    {detail && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="flex items-center gap-2">
                                    <Bank size={18} /> {detail.id}
                                    <Badge className={`text-[10px] ${STATUS_TONE[detail.status] || ""} ml-2`}>{detail.status}</Badge>
                                </DialogTitle>
                            </DialogHeader>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Employee</div>
                                    <div>{detail.employee_name_snapshot}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Policy</div>
                                    <div className="font-mono text-xs">{detail.policy_code_snapshot}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Principal · EMI · Tenure</div>
                                    <div className="font-mono">{fmtMoney(detail.principal)} · {fmtMoney(detail.emi)} · {detail.tenure_months}m</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Outstanding · Paid</div>
                                    <div className="font-mono">{fmtMoney(detail.outstanding_principal)} · {detail.instalments_paid || 0} EMIs</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Total Interest · Repayment</div>
                                    <div className="font-mono text-xs">{fmtMoney(detail.total_interest)} · {fmtMoney(detail.total_repayment)}</div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase text-slate-500 font-semibold">Applicant</div>
                                    <div className="text-xs">{detail.applicant_name} ({detail.applicant_email})</div>
                                </div>
                            </div>
                            {/* Approval/rejection panels */}
                            {detail.approval && (
                                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded text-xs">
                                    <div className="font-semibold text-emerald-800">Approved by {detail.approval.approver_name}</div>
                                    <div className="text-emerald-700">{detail.approval.approved_at}</div>
                                    {detail.approval.note && <div className="text-slate-700 mt-1 italic">&ldquo;{detail.approval.note}&rdquo;</div>}
                                </div>
                            )}
                            {detail.rejection && (
                                <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded text-xs">
                                    <div className="font-semibold text-rose-800">Rejected by {detail.rejection.rejector_name}</div>
                                    <div className="text-slate-700 mt-1">{detail.rejection.reason}</div>
                                </div>
                            )}
                            {detail.disbursement && (
                                <div className="mt-3 p-3 bg-violet-50 border border-violet-200 rounded text-xs">
                                    <div className="font-semibold text-violet-800">Disbursed by {detail.disbursement.disbursed_by_email}</div>
                                    <div className="text-slate-700 mt-1">Ref: {detail.disbursement.reference_number || "—"} · On {detail.disbursement.disbursed_on}</div>
                                </div>
                            )}
                            {detail.foreclosure && (
                                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-300 rounded text-xs">
                                    <div className="font-semibold text-emerald-900">Foreclosed</div>
                                    <div className="font-mono">As of EMI {detail.foreclosure.as_of_instalment} · Mode: {detail.foreclosure.mode}</div>
                                    <div className="font-mono">Foreclose amount: {fmtMoney(detail.foreclosure.foreclose_amount)}</div>
                                </div>
                            )}

                            {/* Action buttons */}
                            <div className="flex gap-2 mt-3 flex-wrap">
                                {detail.status === "Applied" && canApprove && (
                                    <>
                                        <Button size="sm" onClick={() => approveLoan(detail)} data-testid="loan-approve-btn">
                                            <CheckCircle size={14} className="mr-1" /> Approve
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={() => rejectLoan(detail)} data-testid="loan-reject-btn">
                                            <XCircle size={14} className="mr-1" /> Reject
                                        </Button>
                                    </>
                                )}
                                {detail.status === "Approved" && canIssue && (
                                    <Button size="sm" onClick={() => disburseLoan(detail)} data-testid="loan-disburse-btn">
                                        <ArrowsClockwise size={14} className="mr-1" /> Disburse
                                    </Button>
                                )}
                                {(detail.status === "Disbursed" || detail.status === "Active" || detail.status === "Paused") && canIssue && (
                                    <Button size="sm" variant="outline" onClick={() => openForeclose(detail)} data-testid="loan-foreclose-btn">
                                        <Bank size={14} className="mr-1" /> Foreclose
                                    </Button>
                                )}
                                {detail.status === "Active" && canApprove && (
                                    <Button size="sm" variant="outline" onClick={() => pauseLoan(detail)} data-testid="loan-pause-btn">
                                        <Pause size={14} className="mr-1" /> Pause EMI
                                    </Button>
                                )}
                                {detail.status === "Paused" && canApprove && (
                                    <Button size="sm" onClick={() => resumeLoan(detail)} data-testid="loan-resume-btn">
                                        <Play size={14} className="mr-1" /> Resume EMI
                                    </Button>
                                )}
                                {["Approved", "Disbursed", "Active", "Closed", "Foreclosed"].includes(detail.status) && (
                                    <a href={`/loans/${detail.id}/sanction/print`} target="_blank" rel="noreferrer">
                                        <Button size="sm" variant="outline" data-testid="loan-sanction-print-btn">
                                            <FileText size={14} className="mr-1" /> Sanction Letter
                                        </Button>
                                    </a>
                                )}
                            </div>

                            {/* Schedule table */}
                            <div className="mt-4">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Amortisation Schedule</div>
                                <div className="overflow-x-auto border border-slate-200 rounded">
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50 text-slate-600">
                                            <tr>
                                                <th className="text-left px-2 py-1">#</th>
                                                <th className="text-right px-2 py-1">Opening</th>
                                                <th className="text-right px-2 py-1">EMI</th>
                                                <th className="text-right px-2 py-1">Interest</th>
                                                <th className="text-right px-2 py-1">Principal</th>
                                                <th className="text-right px-2 py-1">Closing</th>
                                                <th className="text-left px-2 py-1">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {schedule.map((r) => (
                                                <tr key={r.id} className="border-t border-slate-100 font-mono">
                                                    <td className="px-2 py-1">{r.instalment_no}</td>
                                                    <td className="px-2 py-1 text-right">{fmtMoney(r.opening_balance)}</td>
                                                    <td className="px-2 py-1 text-right">{fmtMoney(r.emi)}</td>
                                                    <td className="px-2 py-1 text-right">{fmtMoney(r.interest_portion)}</td>
                                                    <td className="px-2 py-1 text-right">{fmtMoney(r.principal_portion)}</td>
                                                    <td className="px-2 py-1 text-right">{fmtMoney(r.closing_balance)}</td>
                                                    <td className="px-2 py-1 text-xs">{r.status}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Audit trail */}
                            <div className="mt-4">
                                <div className="text-[10px] uppercase text-slate-500 font-semibold mb-1">Audit Trail</div>
                                <ul className="text-xs space-y-1">
                                    {audit.map((a) => (
                                        <li key={a.id} className="font-mono">
                                            <span className="text-slate-500">{(a.created_at || "").slice(0, 19).replace("T", " ")}</span>
                                            {" · "}
                                            <span className="font-semibold">{a.action}</span>
                                            {a.from_state && <span> ({a.from_state} → {a.to_state})</span>}
                                            <span className="text-slate-500"> · {a.actor_email}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>

            {/* Foreclose dialog */}
            <Dialog open={forecloseOpen} onOpenChange={setForecloseOpen}>
                <DialogContent className="max-w-xl" data-testid="loan-foreclose-dialog">
                    <DialogHeader>
                        <DialogTitle>Foreclose loan {forecloseLoan?.id}</DialogTitle>
                    </DialogHeader>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <Label>As of instalment (paid so far)</Label>
                            <Input type="number" min="0" max={(forecloseLoan?.tenure_months || 1) - 1}
                                value={forecloseInstalment}
                                onChange={(e) => setForecloseInstalment(Number(e.target.value))}
                                data-testid="loan-foreclose-instalment" />
                        </div>
                        <div>
                            <Label>Mode (D15)</Label>
                            <select className="w-full border rounded-md text-sm h-9 px-2 mt-1"
                                value={forecloseMode}
                                onChange={(e) => setForecloseMode(e.target.value)}
                                data-testid="loan-foreclose-mode">
                                {(meta?.foreclose_modes || []).map((m) => <option key={m} value={m}>{m.replaceAll("_", " ")}</option>)}
                            </select>
                        </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={fetchForecloseQuote} className="mt-2">Refresh quote</Button>
                    {forecloseQuote && (
                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm space-y-1">
                            <div><span className="text-slate-500">Outstanding principal:</span> <span className="font-mono">{fmtMoney(forecloseQuote.outstanding_principal)}</span></div>
                            <div><span className="text-slate-500">Interest already paid:</span> <span className="font-mono">{fmtMoney(forecloseQuote.interest_already_paid)}</span></div>
                            <div><span className="text-slate-500">Unaccrued interest:</span> <span className="font-mono">{fmtMoney(forecloseQuote.unaccrued_interest)}</span></div>
                            <div className="border-t border-amber-300 pt-1 font-semibold">
                                Foreclose amount: <span className="font-mono">{fmtMoney(forecloseQuote.foreclose_amount)}</span>
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setForecloseOpen(false)}>Cancel</Button>
                        <Button onClick={submitForeclose} disabled={!forecloseQuote} data-testid="loan-foreclose-submit">Confirm Foreclose</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
